import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import {
  Conditional,
  DealerLocatorSearchForm as DealerLocatorSearchFormBlocks,
  DealerLocatorSearchSuggestions,
  useBreakpoint
} from 'components/pcc-components';
import { PAGE_BREAKPOINTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import {
  DEALER_LOCATOR_EVENT_NAME,
  DEFAULT_NUM_RESULTS,
  FORM_LOCATION_HOMEPAGE,
  SELECTED_STORE_ID,
  TERMS_CHECKED
} from '../constants';
import { useDealerLocatorDrawer } from '../../dealer-locator-drawer-context';
import usePlaces from 'components/dealer-locator/hooks/use-places';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { useDealerLocatorModal } from 'components/dealer-locator/dealer-locator-modal-context';
import { useDealerLocatorLoader } from 'components/dealer-locator/dealer-locator-loader-context';
import { usePathMatch } from 'hooks/use-path-match';
import { getLocationAddress } from '../utils';
import { StoreAddress } from 'components/dealer-locator/types';
import { HEADLESS_HOME_PAGE } from 'constants/links';
import { useEsiteCtx } from 'services/esite/context';
import { PCC_STORELOCATION_CATCORP_USER } from 'constants/featureFlag';
import { useFeatureFlagQuery } from 'services/featureFlag/useFeatureFlagQuery';
import { useParams } from 'next/navigation';
interface DealerLocatorSearchFormProps {
  autoSearch?: boolean;
  location: string;
  setLocation: (search: string) => void;
  getLocations: (search: string) => void;
  setNumDisplayed: (num: number) => void;
  isModal: boolean;
  isSearchLoading?: boolean;
  resetLocationsAndMapToggle: () => void;
  resetFilters: () => void;
  setIsSelectStoreError: (value: boolean) => void;
  storeAddress?: StoreAddress;
}

export const DealerLocatorSearchForm = ({
  autoSearch,
  location,
  setLocation,
  getLocations,
  setNumDisplayed,
  isModal,
  isSearchLoading,
  resetLocationsAndMapToggle,
  resetFilters,
  setIsSelectStoreError,
  storeAddress
}: DealerLocatorSearchFormProps) => {
  const { fireEvent } = useFormSubmitEvent();
  const { userCountryCode, userCity } = useEsiteCtx();
  const { esite } = useParams();
  const context = {
    kind: 'location',
    key: esite,
    esite: esite
  };

  const { data: storeCatcorpFlagData = {} } = useFeatureFlagQuery(
    PCC_STORELOCATION_CATCORP_USER,
    context
  );
  const isStoreCatCorpFlag =
    storeCatcorpFlagData?.[PCC_STORELOCATION_CATCORP_USER] ?? false;
  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const dealerLocatorModal = useDealerLocatorModal();
  const { isDealerLocatorLoading, setIsDealerLocatorLoading } =
    useDealerLocatorLoader();
  const { resetField, setValue } = useFormContext();
  const { getPlaces, suggestions } = usePlaces();
  const isHomePage = usePathMatch(HEADLESS_HOME_PAGE);
  const isFirstTimeOpened = useRef(true);
  const currentUserLocation = useMemo(() => {
    if (!userCity || !userCountryCode) return '';

    return `${userCity}, ${userCountryCode}`;
  }, [userCity, userCountryCode]);

  const [isInputError, setIsInputError] = useState(false);

  const isDrawerOrModalOpen = isModal
    ? dealerLocatorModal?.modalControl.isActive
    : dealerLocatorDrawer?.isDrawerOpen;
  const locationSelected = autoSearch
    ? getLocationAddress(storeAddress)
    : undefined;

  const onSearchHandler = useCallback(
    (searchText?: string) => {
      if (searchText?.trim() === '') {
        setLocation('');
        setIsInputError(true);
      } else {
        resetField(SELECTED_STORE_ID);
        setNumDisplayed(DEFAULT_NUM_RESULTS);
        fireEvent({
          formName: DEALER_LOCATOR_EVENT_NAME,
          formContent: searchText,
          formLocation: isHomePage ? FORM_LOCATION_HOMEPAGE : ''
        });
        setLocation(searchText);
        getLocations(searchText);
      }
    },
    [
      setLocation,
      resetField,
      setNumDisplayed,
      fireEvent,
      isHomePage,
      getLocations
    ]
  );

  const onKeyPressHandler = (text: string) => {
    setLocation(text);
    if (text) {
      getPlaces(text);
    }
  };

  const resetDealerLocator = useCallback(
    (shouldResetFilters?: boolean) => {
      resetLocationsAndMapToggle();
      setLocation('');
      setIsInputError(false);
      setNumDisplayed(DEFAULT_NUM_RESULTS);
      setIsDealerLocatorLoading(false);
      resetField(SELECTED_STORE_ID);
      setValue(TERMS_CHECKED, false);
      setIsSelectStoreError(false);
      if (shouldResetFilters) {
        resetFilters();
      }
    },
    [
      resetLocationsAndMapToggle,
      setNumDisplayed,
      setIsDealerLocatorLoading,
      resetField,
      setValue,
      setIsSelectStoreError,
      resetFilters,
      setLocation
    ]
  );

  // Reset drawer onClose
  useEffect(() => {
    if (!isDrawerOrModalOpen) {
      resetDealerLocator(true);
    }
  }, [isDrawerOrModalOpen, resetDealerLocator]);

  useEffect(() => {
    if (
      isDrawerOrModalOpen &&
      isFirstTimeOpened.current &&
      isStoreCatCorpFlag
    ) {
      isFirstTimeOpened.current = false;
      if (!locationSelected) {
        setLocation(currentUserLocation);
        if (currentUserLocation) {
          getLocations(currentUserLocation);
        }
      }
    }
  }, [
    isDrawerOrModalOpen,
    locationSelected,
    setLocation,
    getLocations,
    currentUserLocation,
    isStoreCatCorpFlag
  ]);
  return (
    <DealerLocatorSearchFormBlocks
      isVisible={isDrawerOrModalOpen}
      placeholder={isMD ? t('ENTER_LOCATION') : undefined}
      value={location}
      onSearch={onSearchHandler}
      onKeyPress={onKeyPressHandler}
      isInputError={isInputError}
      setIsInputError={setIsInputError}
      onCancel={resetDealerLocator}
      isSearchLoading={isSearchLoading}
      isDisabled={isDealerLocatorLoading}
      locationSelected={locationSelected}
      mobileBreakPoint={0}
    >
      <Conditional test={isInputError}>
        <span slot="error" className="mt-1">
          {t('FYD_INVALID_LOCATION')}
        </span>
      </Conditional>
      <DealerLocatorSearchSuggestions
        search={location}
        suggestions={suggestions}
      />
    </DealerLocatorSearchFormBlocks>
  );
};
